import {
  Alert,
  Button,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import {
  createCategory,
  deleteCategory,
  getAllCategories,
  updateCategory,
} from "../../services/category.service";
import CategoryList from "../../ui-components/CategoryList";
import { ModalComponent } from "../../ui-components/Modal";
import SectionTitle from "../../ui-components/SectionTitle";

export default function Category() {
  const [name, setName] = React.useState("");
  const [categories, setCategories] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [categoriesLoading, setCategoriesLoading] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [deactivateId, setDeactivateId] = React.useState<
    number | string | null
  >(null);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState<number | string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<number | string | null>(null);
  const [editName, setEditName] = React.useState("");

  React.useEffect(() => {
    setCategoriesLoading(true);
    getAllCategories()
      .then((x) => setCategories(x))
      .finally(() => setCategoriesLoading(false));
  }, []);

  function handleClick() {
    const trimmed = name.trim();
    if (trimmed.length < 3) {
      setFeedback({
        open: true,
        message: "Informe um nome com pelo menos 3 caracteres.",
        severity: "error",
      });
      return;
    }

    const exists = (categories || []).some(
      (x: any) =>
        x?.active &&
        String(x?.name || "")
          .trim()
          .toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      setFeedback({
        open: true,
        message: "Já existe uma categoria ativa com esse nome.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    createCategory({ name: trimmed })
      .then(() => {
        setFeedback({
          open: true,
          message: "Categoria criada com sucesso!",
          severity: "success",
        });
        setName("");
        getAllCategories().then((x) => setCategories(x));
      })
      .catch(() => {
        setFeedback({
          open: true,
          message: "Não foi possível criar a categoria. Tente novamente.",
          severity: "error",
        });
      })
      .finally(() => setLoading(false));
  }

  function handleToggle(item: any) {
    if (item?.active) {
      setDeactivateId(item.id);
      setDeactivateOpen(true);
    } else {
      setCategoriesLoading(true);
      updateCategory(Number(item.id), { active: true })
        .then(() => {
          setFeedback({ open: true, message: "Categoria ativada com sucesso!", severity: "success" });
          getAllCategories().then((x) => setCategories(x));
        })
        .catch(() =>
          setFeedback({ open: true, message: "Não foi possível ativar a categoria.", severity: "error" })
        )
        .finally(() => setCategoriesLoading(false));
    }
  }

  function handleEdit(item: any) {
    setEditId(item?.id);
    setEditName(item?.name || "");
    setEditOpen(true);
  }

  function handleDelete(item: any) {
    setDeleteId(item?.id);
    setDeleteOpen(true);
  }

  function confirmDeactivate() {
    if (deactivateId == null) return;
    updateCategory(Number(deactivateId), { active: false })
      .then(() => {
        setFeedback({
          open: true,
          message: "Categoria desativada com sucesso!",
          severity: "success",
        });
        setCategoriesLoading(true);
        getAllCategories()
          .then((x) => setCategories(x))
          .finally(() => setCategoriesLoading(false));
      })
      .catch(() =>
        setFeedback({
          open: true,
          message: "Não foi possível desativar a categoria.",
          severity: "error",
        })
      )
      .finally(() => {
        setDeactivateOpen(false);
        setDeactivateId(null);
      });
  }

  function confirmDelete() {
    if (deleteId == null) return;
    // Fallback de "delete": atualizar categoria com nome vazio para esconder da listagem
    setCategoriesLoading(true);
    updateCategory(Number(deleteId), { name: "" })
      .then(() => {
        setFeedback({ open: true, message: "Categoria removida da listagem com sucesso!", severity: "success" });
        getAllCategories()
          .then((x) => setCategories(x))
          .finally(() => setCategoriesLoading(false));
      })
      .catch(() =>
        setFeedback({ open: true, message: "Não foi possível remover a categoria da listagem.", severity: "error" })
      )
      .finally(() => {
        setDeleteOpen(false);
        setDeleteId(null);
      });
  }

  function confirmEdit() {
    const trimmed = editName.trim();
    if (trimmed.length < 3) {
      setFeedback({
        open: true,
        message: "O nome da categoria deve ter pelo menos 3 caracteres.",
        severity: "error",
      });
      return;
    }

    const exists = (categories || []).some(
      (x: any) =>
        x?.active &&
        x?.id !== editId &&
        String(x?.name || "").trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      setFeedback({
        open: true,
        message: "Já existe uma categoria ativa com esse nome.",
        severity: "error",
      });
      return;
    }

    if (editId == null) return;
    setCategoriesLoading(true);
    updateCategory(Number(editId), { name: trimmed })
      .then(() => {
        setFeedback({
          open: true,
          message: "Categoria atualizada com sucesso!",
          severity: "success",
        });
        getAllCategories()
          .then((x) => setCategories(x))
          .finally(() => setCategoriesLoading(false));
      })
      .catch(() =>
        setFeedback({
          open: true,
          message: "Não foi possível atualizar a categoria.",
          severity: "error",
        })
      )
      .finally(() => {
        setEditOpen(false);
        setEditId(null);
        setEditName("");
      });
  }

  return (
    <>
      <div className="flex flex-col gap-6">
        <Paper sx={{ p: 3, maxWidth: "480px", width: "100%", borderRadius: 2 }}>
          <SectionTitle title="Criar categoria" />
          <Grid my={2}>
            <TextField
              label="Nome da categoria"
              variant="outlined"
              size="small"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading) {
                  handleClick();
                }
              }}
            />
          </Grid>
          <Grid>
            <Button
              onClick={handleClick}
              variant="contained"
              size="small"
              fullWidth
              disabled={loading || name.trim().length < 3}
            >
              {loading ? "Criando..." : "Criar Categoria"}
            </Button>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, maxWidth: "480px", width: "100%", borderRadius: 2 }}>
          <SectionTitle title="Todas as categorias"></SectionTitle>
          <Grid my={2}>
            <TextField
              label="Buscar categoria"
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: "block" }}
          >
            {categories?.filter((x: any) => x?.active)?.length || 0} categorias
            ativas
          </Typography>

          <CategoryList
            items={(categories || [])
              .filter((x: any) => String(x?.name || "").trim().length > 0)
              .filter((x: any) =>
                String(x?.name || "")
                  .trim()
                  .toLowerCase()
                  .includes(search.trim().toLowerCase())
              )}
            onToggleActive={handleToggle}
            onEdit={handleEdit}
            onDelete={handleDelete}
            emptyMessage={
              categoriesLoading
                ? "Carregando categorias..."
                : "Nenhuma categoria encontrada."
            }
          />
        </Paper>
      </div>

      <ModalComponent
        open={deactivateOpen}
        handleClose={() => {
          setDeactivateOpen(false);
          setDeactivateId(null);
        }}
        title="Desativar categoria"
        autoHeight
        maxWidth={520}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Tem certeza que deseja desativar a categoria{" "}
          <strong>
            {String(
              (categories || []).find((c: any) => c?.id === deactivateId)
                ?.name || ""
            )}
          </strong>
          ? Ela não aparecerá mais nas listagens ativas.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setDeactivateOpen(false);
                setDeactivateId(null);
              }}
            >
              Cancelar
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              onClick={confirmDeactivate}
            >
              Desativar
            </Button>
          </Grid>
        </Grid>
      </ModalComponent>

      <ModalComponent
        open={deleteOpen}
        handleClose={() => {
          setDeleteOpen(false);
          setDeleteId(null);
        }}
        title="Deletar categoria"
        autoHeight
        maxWidth={520}
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Tem certeza que deseja deletar a categoria{" "}
          <strong>
            {String(
              (categories || []).find((c: any) => c?.id === deleteId)?.name || ""
            )}
          </strong>
          ? Esta ação é permanente e não poderá ser desfeita.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteId(null);
              }}
            >
              Cancelar
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={confirmDelete}
            >
              Deletar
            </Button>
          </Grid>
        </Grid>
      </ModalComponent>

      <ModalComponent
        open={editOpen}
        handleClose={() => {
          setEditOpen(false);
          setEditId(null);
          setEditName("");
        }}
        title="Editar categoria"
        autoHeight
        maxWidth={520}
      >
        <Grid my={1}>
          <TextField
            label="Nome da categoria"
            variant="outlined"
            size="small"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                confirmEdit();
              }
            }}
          />
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setEditOpen(false);
                setEditId(null);
                setEditName("");
              }}
            >
              Cancelar
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              onClick={confirmEdit}
              disabled={editName.trim().length < 3}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </ModalComponent>

      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.severity}
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
}
